import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import signalApi from '../../api/signalApi';
import { SignalResponse } from '../../api/types';
import SignalTable from './components/SignalTable';
import SignalDrawer from './components/SignalDrawer';
import SignalFilters from './components/SignalFilters';
import WeeklySummaryModal from './components/WeeklySummaryModal';
import AdminSignalModal from './components/AdminSignalModal';
import WeeklySnapshotCards from './components/WeeklySnapshotCards';
import { getSignalGroupKey } from './signalGrouping';
import { ChevronDown, CheckCircle, Loader2, ChevronLeft, ChevronRight, Plus, Trash2, RefreshCw } from 'lucide-react';
import userMetadataApi from '../../api/userMetadataApi';
import ChatPanel from '../chat/ChatPanel';
import ChatButton from '../../components/ChatButton';
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';
import { useIsMobile } from '../../utils/hooks';
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { addDays, endOfWeekSunday, formatDateKey, startOfWeekMonday } from '../../utils/tutorialDates';
import { isTutorialRuntimeActive } from '../../components/tutorial/tutorialRuntime';

const buildSignalTutorialSnapshot = (today: Date = new Date()) => {
    const weekStart = startOfWeekMonday(today);
    const weekEnd = endOfWeekSunday(today);

    return {
        business_id: 'tutorial-business',
        week_start: formatDateKey(weekStart),
        week_end: formatDateKey(weekEnd),
        generated_at: today.toISOString(),
        last_week_status: 'On Track',
        last_week_bullets: [
            'Cash movement remained positive',
            'Margin held steady',
            'Ended the week with $93,333 cash',
        ],
        this_week_status: 'Watch',
        this_week_bullets: [
            'Outflows are running ahead of inflows',
            'Vendor D due',
            'Projected ending cash is about $89,833K',
        ],
        key_risks_status: '21 Issues',
        key_risks_bullets: [
            'Partial payment detected for 1004',
            'Payment detected while invoice 1004 remains open',
            '19 additional issues flagged',
        ],
        linked_signal_ids: ['tutorial-signal-1'],
    };
};

const buildSignalTutorialSignals = (today: Date = new Date()): SignalResponse[] => [
    {
        _id: 'tutorial-signal-1',
        type: 'invoice',
        priority: 1,
        impact: '$2,369.52',
        title: 'Partial payment detected',
        details: 'Customer A appears to have paid part of invoice 1004, leaving a residual balance to review.',
        projected_outcome: 'Resolving this item will update AR expectations and improve the cash forecast.',
        resolved: 'open',
        action_link: '/ledger?view=ar',
        created_at: addDays(today, -14).toISOString(),
        event_date: addDays(today, -14).toISOString(),
        metadata: {
            source_system: 'ar',
            detector_id: 'ar_signal:partial_payment_detected',
            status_basis: 'Open AR item',
        },
    },
    {
        _id: 'tutorial-signal-2',
        type: 'invoice',
        priority: 1,
        impact: '$629.10',
        title: 'Expected receipt missed',
        details: 'Customer B invoice 1033 is past its expected receipt date.',
        projected_outcome: 'Following up keeps expected cash from being overstated.',
        resolved: 'open',
        action_link: '/ledger?view=ar',
        created_at: addDays(today, -7).toISOString(),
        event_date: addDays(today, -7).toISOString(),
        metadata: {
            source_system: 'ar',
            detector_id: 'ar_signal:expected_receipt_missed',
        },
    },
    {
        _id: 'tutorial-signal-3',
        type: 'cashflow',
        priority: 2,
        impact: '$45,247.70',
        title: 'Outflow spike ahead',
        details: 'Week 1 expected payments are higher than recent weekly outflows.',
        projected_outcome: 'Reviewing obligations early helps protect near-term cash.',
        resolved: 'open',
        action_link: '/',
        created_at: addDays(today, -4).toISOString(),
        event_date: addDays(today, -4).toISOString(),
        metadata: {
            source_system: 'cashflow',
            detector_id: 'signal_rule:working_capital_squeeze',
        },
    },
];

const getDates = (filter: string) => {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    if (filter === 'this_week') {
        const day = start.getDay() || 7;
        start.setDate(start.getDate() - day + 1);
    } else if (filter === 'last_week') {
        const day = start.getDay() || 7;
        start.setDate(start.getDate() - day - 6);

        const endDay = end.getDay() || 7;
        end.setDate(end.getDate() - endDay);
    } else if (filter === 'this_month') {
        start.setDate(1);
    }

    return {
        start_date: start.toISOString(),
        end_date: end.toISOString()
    };
};

export default function SignalPage() {
    const { isAdmin } = useUser();
    const { workspaces, activeBusinessId, setActiveBusinessId } = useWorkspace();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();

    const [period, setPeriod] = useState<'this_week' | 'last_week' | 'this_month'>('this_week');
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);

    const [showChat, setShowChat] = useState(!isMobile);
    const [extendChat, setExtendChat] = useState(false);
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false);

    const [selectedBusinessId, setSelectedBusinessId] = useState<string | undefined>(undefined);

    const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
    const [showWeeklySummary, setShowWeeklySummary] = useState(false);
    const previousGenerationStatusRef = useRef<string | undefined>(undefined);
    const [isTutorialDemo, setIsTutorialDemo] = useState(() => isTutorialRuntimeActive('home'));

    // Admin Signal Modal states
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [adminSignalToEdit, setAdminSignalToEdit] = useState<SignalResponse | null>(null);

    // Filter states
    // New filter states
    const [activeStatus, setActiveStatus] = useState<'open' | 'closed' | 'archived'>('open');
    const [searchQuery, setSearchQuery] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    // Table state
    const [sortBy, setSortBy] = useState<string>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [hasUserSorted, setHasUserSorted] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const signalTutorialSnapshot = useMemo(() => buildSignalTutorialSnapshot(), []);
    const signalTutorialSignals = useMemo(() => buildSignalTutorialSignals(), []);
    const { data: allBusinessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: !!isAdmin,
    });

    const handleShowChat = () => {
        setShowChat(true)
    }

    useEffect(() => {
        if (isMobile) {
            setShowChat(false);
            setExtendChat(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);

    const periodLabels = {
        'this_week': 'This Week',
        'last_week': 'Last Week',
        'this_month': 'This Month'
    } as const;

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowPeriodFilter(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const userBusinessOptions = Array.from(
        new Map(
            workspaces.flatMap((workspace) =>
                (workspace.businesses || []).map((business) => [
                    business.id,
                    {
                        id: business.id,
                        name: business.legal_name || business.dba_name || 'Unnamed Business',
                    },
                ]),
            ),
        ).values(),
    );
    const adminBusinessOptions = allBusinessesData?.businesses.map((b) => ({
        id: b.business_id || b.user_id,
        name: b.legal_name || b.dba_name || b.onboarding?.qbo_metadata?.company_name || b.user_email || 'Unknown',
        subtitle: b.user_email || undefined,
    })) || [];
    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;

    useEffect(() => {
        if (activeBusinessId) {
            setSelectedBusinessId(activeBusinessId);
        }
    }, [activeBusinessId, businessOptions]);
    const effectiveBusinessId = selectedBusinessId || activeBusinessId || undefined;

    useEffect(() => {
        const handleTutorialStep = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'home' || detail?.pageKey === 'signals') {
                setIsTutorialDemo(true);
                setActiveStatus('open');
                setSearchQuery('');
                setPriorityFilter('');
                setTypeFilter('');
                setSelectedIds([]);
            }
        };
        const handleTutorialActive = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string; active?: boolean } | undefined;
            const active = detail?.active !== false && detail?.pageKey === 'home';
            setIsTutorialDemo(active);
            if (active) {
                setActiveStatus('open');
                setSearchQuery('');
                setPriorityFilter('');
                setTypeFilter('');
                setSelectedIds([]);
            }
        };
        const handleTutorialClosed = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'home' || detail?.pageKey === 'signals') {
                setIsTutorialDemo(false);
                setSelectedSignalId(null);
            }
        };
        if (isTutorialRuntimeActive('home')) {
            setIsTutorialDemo(true);
            setActiveStatus('open');
            setSearchQuery('');
            setPriorityFilter('');
            setTypeFilter('');
            setSelectedIds([]);
        }
        window.addEventListener('finz:tutorial:active', handleTutorialActive);
        window.addEventListener('finz:tutorial:step', handleTutorialStep);
        window.addEventListener('finz:tutorial:closed', handleTutorialClosed);
        return () => {
            window.removeEventListener('finz:tutorial:active', handleTutorialActive);
            window.removeEventListener('finz:tutorial:step', handleTutorialStep);
            window.removeEventListener('finz:tutorial:closed', handleTutorialClosed);
        };
    }, []);

    const { start_date, end_date } = getDates(period);

    const { data: generationStatus } = useQuery({
        queryKey: ['signal-generation-status', effectiveBusinessId],
        queryFn: () => signalApi.getSignalGenerationStatus(effectiveBusinessId),
        enabled: !!effectiveBusinessId,
        refetchInterval: (query) => (
            query.state.data?.status === 'generating' ? 2000 : false
        ),
    });

    const { data: signalCounts } = useQuery({
        queryKey: ['signal-counts', effectiveBusinessId, period],
        queryFn: () => signalApi.getSignalCounts({
            business_id: effectiveBusinessId,
            start_date,
            end_date,
        }),
        enabled: !!effectiveBusinessId,
    });

    const { data: homeSnapshot, isLoading: isHomeSnapshotLoading } = useQuery({
        queryKey: ['home-snapshot', effectiveBusinessId],
        queryFn: () => signalApi.getHomeSnapshot(effectiveBusinessId),
        enabled: !!effectiveBusinessId,
    });

    // Fetch signals
    const { data: signals = [], isLoading, isFetching } = useQuery({
        queryKey: ['signals', effectiveBusinessId, period, activeStatus],
        queryFn: () => signalApi.getSignals({
            business_id: effectiveBusinessId,
            start_date,
            end_date,
            resolved: activeStatus,
        }),
        enabled: !!effectiveBusinessId
    });

    // Generate signals mutation — triggers backend processing then refetches
    const generateMutation = useMutation({
        mutationFn: () => signalApi.generateSignals(effectiveBusinessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['signal-generation-status'] });
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
            queryClient.invalidateQueries({ queryKey: ['home-snapshot'] });
        }
    });

    const generationStartedAt = generationStatus?.started_at || generationStatus?.updated_at || null;
    const generationStartedAtMs = generationStartedAt ? new Date(generationStartedAt).getTime() : 0;
    const isStaleGeneratingStatus = Boolean(
        generationStatus?.status === 'generating'
        && generationStartedAtMs
        && (Date.now() - generationStartedAtMs) > 90_000
    );
    const isSignalGenerating = (generationStatus?.status === 'generating' && !isStaleGeneratingStatus) || generateMutation.isPending;
    const visibleSignals = isTutorialDemo ? signalTutorialSignals : isSignalGenerating ? [] : signals;
    const displaySignalCounts = isTutorialDemo
        ? { open: signalTutorialSignals.length, closed: 0, archived: 0 }
        : signalCounts;
    const displayHomeSnapshot = isTutorialDemo ? signalTutorialSnapshot : homeSnapshot;
    const displayHomeSnapshotLoading = isTutorialDemo ? false : isHomeSnapshotLoading;
    const signalMutation = useMutation({
        mutationFn: ({ id, updates, bId }: { id: string, updates: Partial<SignalResponse>, bId?: string }) =>
            signalApi.updateSignal(id, updates, bId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
        }
    });

    const batchStatusMutation = useMutation({
        mutationFn: ({ ids, businessId }: { ids: string[]; businessId?: string }) =>
            signalApi.batchUpdateSignals({
                signal_ids: ids,
                resolved: 'closed',
                business_id: businessId,
            }),
        onSuccess: () => {
            setSelectedIds([]);
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
        }
    });

    const deleteSignalMutation = useMutation({
        mutationFn: (id: string) => signalApi.deleteSignal(id),
        onSuccess: () => {
            setSelectedIds(prev => prev.filter(Boolean));
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
        }
    });

    const batchDeleteMutation = useMutation({
        mutationFn: (ids: string[]) =>
            Promise.all(ids.map(id => signalApi.deleteSignal(id))),
        onSuccess: () => {
            setSelectedIds([]);
            queryClient.invalidateQueries({ queryKey: ['signals'] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] });
        }
    });

    const handleStatusChange = (id: string, status: 'open' | 'closed' | 'archived') => {
        if (isTutorialDemo) return;
        signalMutation.mutate({ id, updates: { resolved: status }, bId: effectiveBusinessId });
    };

    const handleDateChange = (id: string, date: Date | null) => {
        if (isTutorialDemo) return;
        signalMutation.mutate({
            id,
            updates: { due_on: date ? date.toISOString() : undefined },
            bId: effectiveBusinessId
        });
    };

    const handleDelete = (id: string) => {
        if (isTutorialDemo) return;
        const ok = window.confirm('Delete this signal permanently?');
        if (!ok) return;
        deleteSignalMutation.mutate(id);
        if (selectedSignalId === id) {
            setSelectedSignalId(null);
        }
        setSelectedIds((prev) => prev.filter((sid) => sid !== id));
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setPriorityFilter('');
        setTypeFilter('');
        setPeriod('this_week');
        setSelectedIds([]);
    };

    const handleSort = (column: string) => {
        setHasUserSorted(true);
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    // Client-side filtering and sorting
    const filteredSignals = isTutorialDemo ? [...signalTutorialSignals] : Array.isArray(visibleSignals) ? [...visibleSignals]
        .filter((signal: SignalResponse) => {
            const matchesSearch = searchQuery === '' ||
                (signal.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (signal.details || '').toLowerCase().includes(searchQuery.toLowerCase());

            const matchesPriority = priorityFilter === '' || String(signal.priority) === priorityFilter;
            const matchesType = typeFilter === '' || getSignalGroupKey(signal) === typeFilter;

            return matchesSearch && matchesPriority && matchesType;
        })
        .sort((a, b) => {
            let fieldA: any = a[sortBy as keyof SignalResponse];
            let fieldB: any = b[sortBy as keyof SignalResponse];

            if (sortBy === 'date') {
                const aRelevantDate = a.resolved === 'open'
                    ? (a.event_date || a.due_on || a.created_at)
                    : a.created_at;
                const bRelevantDate = b.resolved === 'open'
                    ? (b.event_date || b.due_on || b.created_at)
                    : b.created_at;
                fieldA = new Date(aRelevantDate || 0).getTime();
                fieldB = new Date(bRelevantDate || 0).getTime();
            }

            if (fieldA < fieldB) return sortOrder === 'asc' ? -1 : 1;
            if (fieldA > fieldB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        }) : [];

    const statusTabs = [
        { id: 'open', label: 'Open', count: displaySignalCounts?.open ?? 0 },
        { id: 'closed', label: 'Closed', count: displaySignalCounts?.closed ?? 0 },
        { id: 'archived', label: 'Archived', count: displaySignalCounts?.archived ?? 0 }
    ] as const;

    const selectedSignal = Array.isArray(visibleSignals) ? (visibleSignals.find((s: SignalResponse) => s._id === selectedSignalId) || null) : null;
    useEffect(() => {
        const previousStatus = previousGenerationStatusRef.current;
        if (generationStatus?.status === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['signals', effectiveBusinessId, period] });
            queryClient.invalidateQueries({ queryKey: ['signal-counts', effectiveBusinessId, period] });
            if (previousStatus === 'generating') {
                queryClient.invalidateQueries({ queryKey: ['weekly-summary', effectiveBusinessId] });
                queryClient.invalidateQueries({ queryKey: ['weekly-summary-banner', effectiveBusinessId] });
                queryClient.invalidateQueries({ queryKey: ['home-snapshot', effectiveBusinessId] });
            }
        }
        previousGenerationStatusRef.current = generationStatus?.status;
    }, [generationStatus?.status, queryClient, effectiveBusinessId, period]);

    return (
        <div className="h-full flex overflow-hidden relative">
            <WeeklySummaryModal
                open={showWeeklySummary}
                businessId={effectiveBusinessId}
                onClose={() => setShowWeeklySummary(false)}
                showSignalsAction={false}
                isAdmin={isAdmin}
            />

            {/* Main Content */}
            <div className={`flex min-h-0 flex-col flex-1 overflow-y-auto overflow-x-hidden ${showChat && !showSetupGuidePanel && !isMobile ? (extendChat ? 'w-[60%]' : 'w-[70%]') : 'w-full'}`}>
                <div className="min-w-0 mx-5 mb-5 mt-[16px]">
                    <div className="mb-2 flex flex-row flex-wrap items-center justify-between gap-3">
                        <div className="flex h-10 w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
                            <h1 className="text-2xl font-semibold">Home</h1>
                            <button
                                onClick={() => {
                                    if (!isTutorialDemo) generateMutation.mutate();
                                }}
                                disabled={isSignalGenerating || isFetching}
                                aria-label="Refresh"
                                title="Refresh"
                                className="relative flex h-10 w-10 items-center justify-center rounded border bg-indigo-500 text-sm text-white shadow-sm hover:bg-indigo-600 disabled:opacity-80 sm:hidden"
                            >
                                <RefreshCw size={16} className={isSignalGenerating || isFetching ? 'animate-spin' : ''} />
                            </button>
                        </div>
                        <div className="flex w-auto flex-wrap items-center justify-end gap-3">
                            {/* Period Selector */}
                            {/* <div className="relative flex w-full items-center rounded border border-gray-300 px-4 py-4 shadow-sm sm:w-auto" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                                    className="flex h-full w-full items-center justify-between gap-2 text-sm leading-none focus:outline-none sm:w-auto"
                                >
                                    <span>{periodLabels[period]}</span>
                                    <ChevronDown size={14} className="text-gray-400" />
                                </button>

                                {showPeriodFilter && (
                                    <div className="dropdown-container top-12 right-0 absolute bg-white border rounded shadow-lg z-50 overflow-hidden">
                                        {(['this_week', 'last_week', 'this_month'] as const).map((value) => (
                                            <button
                                                key={value}
                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none transition-colors flex items-center h-10"
                                                onClick={() => {
                                                    setPeriod(value);
                                                    setShowPeriodFilter(false);
                                                }}
                                            >
                                                {periodLabels[value]}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div> */}

                            {businessOptions.length > 1 && (
                                <SearchableBusinessSelect
                                    options={businessOptions}
                                    value={activeBusinessId || selectedBusinessId}
                                    onChange={(id) => {
                                        setSelectedBusinessId(id);
                                        setActiveBusinessId(id);
                                    }}
                                    widthClassName="w-full sm:w-80"
                                    showSubtitleWhenClosed={false}
                                />
                            )}

                            {isAdmin && (
                                <div className="w-full sm:w-auto">
                                    <button
                                        onClick={() => setShowWeeklySummary(true)}
                                        disabled={!effectiveBusinessId}
                                        className="relative w-full rounded border border-gray-300 bg-white px-6 py-3 text-sm text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                                    >
                                        Weekly Summary
                                    </button>
                                </div>
                            )}

                            <div className="hidden sm:block sm:w-auto">
                                <button
                                    onClick={() => {
                                        if (!isTutorialDemo) generateMutation.mutate();
                                    }}
                                    disabled={isSignalGenerating || isFetching}
                                    aria-label="Refresh"
                                    title="Refresh"
                                    className={`relative flex h-11 w-10 items-center justify-center rounded border text-sm shadow-sm sm:w-auto sm:px-6 ${isSignalGenerating || isFetching ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}
                                >
                                    <span>Refresh</span>
                                </button>
                            </div>
                            {isAdmin && (
                                <div className="w-full sm:w-auto">
                                    <button
                                        onClick={() => {
                                            setAdminSignalToEdit(null);
                                            setAdminModalOpen(true);
                                        }}
                                        className="relative flex w-full items-center justify-center gap-2 rounded border bg-gray-800 px-4 py-3 text-sm text-white shadow-sm hover:bg-gray-900 sm:w-auto"
                                    >
                                        <Plus size={16} /> Add Signal
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className={`bg-gray-50/30 px-5 pb-8 flex flex-col ${isMobile ? 'flex-none pb-24' : 'flex-1 min-h-0'}`}>
                    <WeeklySnapshotCards
                        snapshot={displayHomeSnapshot}
                        isLoading={displayHomeSnapshotLoading}
                    />

                    <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        {/* Status Tabs */}
                        <div className="flex items-center gap-8">
                            {statusTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`pb-4 text-sm font-bold transition-all relative ${activeStatus === tab.id
                                        ? "text-gray-700"
                                        : "text-gray-500 hover:text-gray-600"
                                        }`}
                                    onClick={() => setActiveStatus(tab.id as any)}
                                >
                                    {tab.label} ({tab.count})
                                    {activeStatus === tab.id && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#6466FA] rounded-full" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Filter Card */}
                        <SignalFilters
                            search={searchQuery}
                            onSearchChange={setSearchQuery}
                            priority={priorityFilter}
                            onPriorityChange={setPriorityFilter}
                            type={typeFilter}
                            onTypeChange={setTypeFilter}
                            onClearFilters={handleClearFilters}
                            className="flex flex-wrap items-center justify-start gap-4 lg:justify-end"
                        />
                    </div>

                    {!isTutorialDemo && generationStatus?.status === 'generating' && isStaleGeneratingStatus && (
                        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
                            Signal generation appears stuck from an older backend run. Showing the last available signals instead.
                        </div>
                    )}

                    <div data-tutorial="signal-table" className={`relative bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col overflow-hidden ${isMobile ? 'min-h-[420px] flex-none' : 'flex-1 min-h-[360px]'}`}>
                        <SignalTable
                            signals={filteredSignals}
                            isLoading={isTutorialDemo ? false : isLoading}
                            onRowClick={(id) => {
                                setSelectedSignalId(id);
                            }}
                            onStatusChange={handleStatusChange}
                            onDelete={handleDelete}
                            onDateChange={handleDateChange}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            showSortArrow={hasUserSorted}
                            selectedIds={selectedIds}
                            onSelectionChange={setSelectedIds}
                            showTutorialFallback={isTutorialDemo}
                            isGenerating={!isTutorialDemo && isSignalGenerating}
                        />

                        {/* Batch Action Bar */}
                        {!isTutorialDemo && selectedIds.length > 0 && (
                            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white shadow-lg border border-gray-200 rounded-lg px-6 py-3 flex items-center gap-6 z-[1001] animate-in fade-in slide-in-from-bottom-4 duration-200">
                                <span className="text-sm font-medium text-gray-700">
                                    {selectedIds.length} signal{selectedIds.length !== 1 ? 's' : ''} selected
                                </span>

                                <div className="h-6 w-px bg-gray-200"></div>

                                <button
                                    onClick={() => batchStatusMutation.mutate({ ids: selectedIds, businessId: effectiveBusinessId })}
                                    disabled={batchStatusMutation.isPending || batchDeleteMutation.isPending}
                                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {batchStatusMutation.isPending ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} />
                                            Close
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={() => {
                                        if (window.confirm(`Permanently delete ${selectedIds.length} signal${selectedIds.length !== 1 ? 's' : ''}?`)) {
                                            batchDeleteMutation.mutate(selectedIds);
                                        }
                                    }}
                                    disabled={batchDeleteMutation.isPending || batchStatusMutation.isPending}
                                    className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {batchDeleteMutation.isPending ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} />
                                            Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {!isAdmin && showSetupGuidePanel && !isMobile && (
                <div
                    aria-hidden="true"
                    className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                />
            )}

            {/* Chat Panel */}
            {!isAdmin && <div className={`${showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-[1999] flex bg-white w-full max-w-none h-[100dvh]' : 'flex bg-white') : 'hidden'} ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'} chat-panel`}>
                <div className="flex-1 overflow-auto flex flex-col">
                    <ChatPanel
                        page={'signals'}
                        initialPrompts={[
                            'What signal needs to resolve first?',
                            'Walk me through the AP signals.',
                            'Walk me through the AR signals.',
                        ]}
                        onClose={() => setShowChat(false)}
                    />
                </div>

                {/* Extend Button */}
                {!isMobile && (
                    <div onClick={() => setExtendChat(!extendChat)} className="border hover:bg-gray-200 hover:cursor-pointer py-4 rounded absolute bg-gray-100 top-20 -left-2">
                        {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                    </div>
                )}
            </div>}

            {/* Chat Toggle Button */}
            {(!showChat && !showSetupGuidePanel && !isAdmin) && <ChatButton handleShowChat={handleShowChat} />}

            <SignalDrawer
                signal={selectedSignal}
                open={!!selectedSignalId}
                onClose={() => setSelectedSignalId(null)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onDateChange={handleDateChange}
                onEdit={(s) => {
                    setAdminSignalToEdit(s);
                    setAdminModalOpen(true);
                }}
            />

            {isAdmin && (
                <AdminSignalModal
                    open={adminModalOpen}
                    onClose={() => {
                        setAdminModalOpen(false);
                        setAdminSignalToEdit(null);
                    }}
                    signal={adminSignalToEdit}
                    businessId={effectiveBusinessId}
                />
            )}
        </div>
    );
}
